import {
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  Skeleton,
} from '@mui/material';

import { IDossierViewModel } from 'viewModels';
import {
  CloseButton,
  ModalContainer,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle,
  ModalTitleContainer,
} from './styles';
import { useEffect, useState } from 'react';
import { useDossier } from 'contexts';
import { DossierStatus, SituationType } from 'types/enums';
import { X } from 'lucide-react';

interface SummaryDossierProps {
  open: boolean;
  onClose: () => void;
  dossier: IDossierViewModel;
}

const SummaryDossier: React.FC<SummaryDossierProps> = ({
  dossier,
  open,
  onClose,
}: SummaryDossierProps) => {
  const theme = useTheme();
  const { getDossierSummary, loadingDossierSummary } = useDossier();
  const [localDossier, setLocalDossier] =
    useState<IDossierViewModel>(dossier);

  useEffect(() => {
    const fetchSummary = async () => {
      if (open && !dossier.summaryDossier) {
        const summary = await getDossierSummary(dossier.id);
        if (summary) {
          setLocalDossier((prev) => ({
            ...prev,
            summaryDossier: summary,
          }));
        }
      }
    };

    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dossier]);

  const getStatusInfo = (success?: boolean) => {
    if (
      success === undefined ||
      success === null ||
      localDossier.status === DossierStatus.PROCESSING ||
      localDossier.status === DossierStatus.PENDING ||
      localDossier.status === DossierStatus.CANCELED
    ) {
      return { text: '-', color: theme.palette.text.secondary };
    }
    return success
      ? { text: 'Sucesso', color: theme.palette.success.main }
      : { text: 'Falha', color: theme.palette.error.main };
  };

  const getPendencyInfo = (situation?: SituationType) => {
    if (
      situation === undefined ||
      situation === null ||
      localDossier.status === DossierStatus.PROCESSING ||
      localDossier.status === DossierStatus.PENDING ||
      localDossier.status === DossierStatus.CANCELED
    ) {
      return {
        text: '-',
        tooltip: '',
        color: theme.palette.text.secondary,
      };
    }
    switch (situation) {
      case SituationType.HAS_PENDING:
        return {
          text: 'Com Pendências',
          tooltip: 'Pendências encontradas para o documento.',
          color: theme.palette.warning.main,
        };
      case SituationType.NO_PENDING:
        return {
          text: 'Sem Pendências',
          tooltip: 'Nenhuma pendência encontrada.',
          color: theme.palette.success.main,
        };
      case SituationType.NOT_OBTAINED:
        return {
          text: 'Dados indisponíveis',
          tooltip:
            'Nenhuma informação disponível para o documento foi encontrado na fonte.',
          color: theme.palette.text.secondary,
        };
      case SituationType.RELEVANT_INFO:
        return {
          text: 'Informações Relevantes',
          tooltip:
            'Informações relevantes foram coletadas sem pendências.',
          color: theme.palette.info.main,
        };
      case SituationType.COLLECT_ERROR:
        return {
          text: 'Falha ao coletar',
          tooltip: 'Erro ao coletar dados.',
          color: theme.palette.error.main,
        };
      default:
        return {
          text: '-',
          tooltip: '',
          color: theme.palette.text.secondary,
        };
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          outline: 'none',
          width: { xs: '90vw', sm: '80vw', md: '60vw' },
          minHeight: { xs: '70vh', sm: '80vh' },
          maxWidth: '100%',
        }}
      >
        <ModalContainer>
          <ModalHeader>
            <ModalTitleContainer>
              <ModalTitle id="modal-title">
                Resumo do Dossiê
              </ModalTitle>
              <ModalDescription id="modal-description">
                Visualize o resumo do dossiê e as informações
                coletadas de cada fonte.
              </ModalDescription>
            </ModalTitleContainer>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalContent>
            <Table aria-label="listDossiers">
              <TableHead>
                <TableRow
                  sx={{
                    th: {
                      backgroundColor: theme.palette.background.paper,
                      '& p': {
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <TableCell width="70%">
                    <Typography>Fonte</Typography>
                  </TableCell>
                  <TableCell width="10%" align="center">
                    <Typography>Status</Typography>
                  </TableCell>
                  <TableCell width="20%" align="center">
                    <Typography>Pendências</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              {loadingDossierSummary ? (
                <TableBody>
                  {dossier.selectedExternalFonts.map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell width="70%">
                        <Skeleton variant="text" width="80%" />
                      </TableCell>
                      <TableCell width="10%" align="center">
                        <Skeleton variant="text" width="60%" />
                      </TableCell>
                      <TableCell width="20%" align="center">
                        <Skeleton variant="text" width="60%" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  {localDossier.summaryDossier?.map((font) => {
                    const status = getStatusInfo(font.success);
                    const pendency = getPendencyInfo(font.situation);

                    return (
                      <TableRow
                        data-testid="tableRowDossier"
                        key={font.key}
                        sx={{
                          '&:last-child td, &:last-child th': {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell width="70%">
                          <Tooltip
                            title={font.name}
                            placement="top-start"
                          >
                            <Typography>{font.name}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell width="10%" align="center">
                          <Typography sx={{ color: status.color }}>
                            {status.text}
                          </Typography>
                        </TableCell>
                        <TableCell width="20%" align="center">
                          <Tooltip
                            title={pendency.tooltip}
                            placement="top"
                          >
                            <Typography
                              sx={{ color: pendency.color }}
                            >
                              {pendency.text}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
          </ModalContent>
        </ModalContainer>
      </Paper>
    </Modal>
  );
};

export default SummaryDossier;
